import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { createChannel, fetchChannels } from '../../store/slices/channelSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { API_URL } from '../../config/api';
import { fileForUpload } from '../../utils/uploadFilename';
import PresignedAvatar from '../common/PresignedAvatar';

const CreateChannelModal = ({ isOpen, onClose, onChannelCreated }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.channels);
  const { users, loading: usersLoading } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);
  const isGlobalOrgUser = currentUser?.globalOrganization === true;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    isPrivate: true,
  });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isOpen) {
      // Fetch users when modal opens
      dispatch(fetchUsers());
      // Reset form
      setFormData({ name: '', description: '', imageUrl: '', isPrivate: true });
      setSelectedUserIds([]);
      setSearchQuery('');
    }
  }, [isOpen, dispatch, isGlobalOrgUser]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select an image file'
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      dispatch(addNotification({
        type: 'error',
        message: 'Image size should be less than 5MB'
      }));
      return;
    }

    try {
      setIsUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', fileForUpload(file));

      const uploadResponse = await axios.post(`${API_URL}/files/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = uploadResponse.data.fileUrl;
      setFormData(prev => ({ ...prev, imageUrl: fileUrl }));

      dispatch(addNotification({
        type: 'success',
        message: 'Channel picture uploaded successfully'
      }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to upload channel picture'
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // If switching to private, validate user selection
    if (name === 'isPrivate' && checked && selectedUserIds.length < 1) {
      // Will be validated on submit
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const removeSelectedUser = (userId) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const username = user.username.toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || username.includes(query);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Channel name is required',
      }));
      return;
    }

    // Validate channel name: lowercase, alphanumeric and hyphens/underscores only
    const channelName = formData.name.trim().toLowerCase().replace(/\s+/g, '-');
    const nameRegex = /^[a-z0-9-_]+$/;
    
    if (!nameRegex.test(channelName)) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Channel name can only contain lowercase letters, numbers, hyphens, and underscores',
      }));
      return;
    }

    if (channelName.length < 3 || channelName.length > 50) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Channel name must be between 3 and 50 characters',
      }));
      return;
    }

    // Validate private channel requires at least 2 users (including creator = 1 selected)
    const requiresPrivateMembers = formData.isPrivate || isGlobalOrgUser;
    if (requiresPrivateMembers && selectedUserIds.length < 1) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Private channel must have at least 2 members. Please select at least 1 user.',
      }));
      return;
    }

    try {
      const newChannel = await dispatch(createChannel({
        name: channelName,
        description: formData.description.trim(),
        imageUrl: formData.imageUrl,
        isPrivate: isGlobalOrgUser ? true : formData.isPrivate,
        memberIds: selectedUserIds,
      })).unwrap();
      
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: `Channel "${channelName}" created successfully!`,
      }));
      
      // Refresh channels list to show the new channel
      dispatch(fetchChannels());
      
      // Reset form and close modal
      setFormData({ name: '', description: '', imageUrl: '', isPrivate: true });
      setSelectedUserIds([]);
      setSearchQuery('');
      onClose();
      
      // Select the newly created channel
      if (onChannelCreated && newChannel) {
        onChannelCreated(newChannel);
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof err === 'string' ? err : 'Failed to create channel',
      }));
    }
  };

  const getSelectedUsers = () => {
    return users.filter(user => selectedUserIds.includes(user.id));
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div 
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg lg:max-w-2xl transform transition-all overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 sm:p-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Create Channel
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Channel Profile Picture */}
              <div className="flex flex-col items-center justify-center">
                <div className="relative group">
                  <PresignedAvatar
                    fileKey={formData.imageUrl}
                    name={formData.name || 'New Channel'}
                    initials={formData.name ? formData.name.substring(0, 2).toUpperCase() : 'CH'}
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 shadow-sm"
                    fallbackClassName="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center text-white text-2xl font-bold border-2 border-gray-200 dark:border-gray-700 shadow-sm"
                  />
                  <label className="absolute bottom-0 right-0 bg-primary-600 text-white rounded-full p-2 cursor-pointer hover:bg-primary-700 shadow-md transition-all hover:scale-110">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                  {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-full">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Channel Profile Picture
                </p>
              </div>

              {/* Form Fields Container */}
              <div className="space-y-4">
                {/* Channel Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Channel Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. general, random, development"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    maxLength={50}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Lowercase, no spaces or special characters
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="What is this channel about?"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    maxLength={200}
                  />
                </div>

                {/* Privacy Toggle */}
                {isGlobalOrgUser ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Global organization accounts can only create private channels for direct collaboration.
                  </p>
                ) : (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPrivate"
                      name="isPrivate"
                      checked={formData.isPrivate}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                    />
                    <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      Make this channel private
                    </label>
                  </div>
                )}
                {!isGlobalOrgUser && !formData.isPrivate ? (
                  <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                    Everyone in the organization can access this channel. People you have not added will not see it until they are mentioned.
                  </p>
                ) : null}

                {/* User Selection Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {formData.isPrivate || isGlobalOrgUser ? (
                        <>
                          Select Members <span className="text-red-500">*</span>
                        </>
                      ) : (
                        <>Add Members (optional)</>
                      )}
                    </label>
                    {selectedUserIds.length > 0 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedUserIds.length} selected
                      </span>
                    )}
                  </div>

                  {/* Selected Users */}
                  {selectedUserIds.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2 max-h-32 overflow-y-auto custom-scrollbar">
                      {getSelectedUsers().map(user => (
                        <div
                          key={user.id}
                          className="flex items-center space-x-2 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full text-sm"
                        >
                          <span className="truncate max-w-[150px]">{user.firstName} {user.lastName}</span>
                          <button
                            type="button"
                            onClick={() => removeSelectedUser(user.id)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search Users */}
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Users List */}
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg custom-scrollbar">
                    {usersLoading ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Loading users...
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        No users found
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200 dark:divide-gray-600">
                        {filteredUsers.map(user => (
                          <label
                            key={user.id}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => handleUserToggle(user.id)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                            />
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <PresignedAvatar
                                fileKey={user.profilePicture}
                                name={`${user.firstName} ${user.lastName}`}
                                initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                fallbackClassName="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  @{user.username}
                                </p>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 dark:bg-red-900 p-3">
                  <p className="text-sm text-red-700 dark:text-red-200">
                    {typeof error === 'string' ? error : 'An error occurred'}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name.trim() || ((formData.isPrivate || isGlobalOrgUser) && selectedUserIds.length < 1)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateChannelModal;